import React, { useEffect, useMemo, useState } from "react";
import { View, TouchableOpacity, ScrollView, StyleSheet } from "react-native";
import Text from "@components/CustomText";
import BottomSheet from "@components/ds/BottomSheet";
import SheetButtons from "@components/ds/SheetButtons";
import useRouteCatalog from "../hooks/useRouteCatalog";
import { tokens } from "../styles";

// Multi-select sheet for picking the routes a DB will deliver. Replaces the
// old single-add settings flow.
//
// Visual states:
//   • Unassigned to anyone     → standard surface
//   • Already on THIS DB       → checkbox pre-checked, success tint
//   • On a DIFFERENT DB        → selectable but grey + "On: <db name>"
//                                 because web allows the same route on
//                                 multiple DBs (cp_db_routes_v2 has no
//                                 unique constraint).
//
// On Save, the parent batches addDBRoute / deleteDBRoute calls by diffing
// the initial assignment vs the new selection.

const RoutePickerSheet = ({
  visible,
  db,
  cpDbs = [],
  onClose,
  onSave,
  saving,
}) => {
  const { routes, loading } = useRouteCatalog({ enabled: visible });

  // Selection identity is route_name, NOT route_id. The backend's
  // /dc-routing-packing/v1/localities catalog returns route_id = locality_string
  // (e.g. "GGN 1"), while /dc-routing-packing/v1/dbs-snapshot returns
  // db.routes[].route_id = bigint from cp_db_routes_v2. Matching by id
  // therefore desyncs after the first save → reload (catalog still serves
  // "GGN 1", db now has 235). route_name ("GGN 1" on both) is the only
  // consistent identifier across the two endpoints.
  const initialIds = useMemo(() => {
    const arr = Array.isArray(db?.routes) ? db.routes : [];
    return new Set(arr.map((r) => String(r?.route_name)).filter(Boolean));
  }, [db]);

  // Stringified id list — used as a stable dep so we re-sync `selected` after
  // a save → reload cycle (when `db.routes` content changes) without forcing
  // the sheet to close. Critical for partial-failure recovery: after a save
  // where some adds/removes failed, the user must see the *post-reload*
  // truth so re-saving doesn't replay diffs that already succeeded.
  const initialIdsKey = useMemo(
    () => Array.from(initialIds).sort().join(","),
    [initialIds],
  );

  const [selected, setSelected] = useState(initialIds);

  useEffect(() => {
    if (visible) setSelected(initialIds);
    // initialIds itself is a new Set every render, so depend on the stable
    // string key instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, initialIdsKey]);

  // routeName → array of OTHER db names that already have this route assigned.
  const assignedElsewhereByRoute = useMemo(() => {
    const map = new Map();
    cpDbs.forEach((d) => {
      if (String(d?.cp_db_id) === String(db?.cp_db_id)) return;
      const arr = Array.isArray(d?.routes) ? d.routes : [];
      arr.forEach((r) => {
        const rname = String(r?.route_name || "");
        if (!rname) return;
        const list = map.get(rname) || [];
        if (d?.db_name) list.push(d.db_name);
        map.set(rname, list);
      });
    });
    return map;
  }, [cpDbs, db]);

  const toggle = (rid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      return next;
    });
  };

  const dirty = useMemo(() => {
    if (selected.size !== initialIds.size) return true;
    let same = true;
    selected.forEach((id) => {
      if (!initialIds.has(id)) same = false;
    });
    return !same;
  }, [selected, initialIds]);

  const handleConfirm = () => {
    if (!dirty || !onSave) return;
    const toAdd = [];
    const toRemove = [];
    // toAdd lookups by route_name — the catalog's route_id (locality string
    // like "GGN 1") is sent as a placeholder; backend createRoute ignores it
    // and only consumes route_name.
    selected.forEach((rname) => {
      if (!initialIds.has(rname)) {
        const route = routes.find((r) => String(r?.route_name) === rname);
        if (route)
          toAdd.push({
            route_id: route.route_id,
            route_name: route.route_name || `Route ${route.route_id}`,
          });
      }
    });
    // toRemove must carry the REAL bigint route_id from cp_db_routes_v2,
    // since deleteRoute keys off route_id. Look up the live db.routes entry
    // by route_name to recover that id.
    const dbRoutesByName = new Map(
      (Array.isArray(db?.routes) ? db.routes : []).map((r) => [
        String(r?.route_name),
        r,
      ]),
    );
    initialIds.forEach((rname) => {
      if (!selected.has(rname)) {
        const dbRoute = dbRoutesByName.get(rname);
        if (dbRoute?.route_id != null) {
          toRemove.push({
            route_id: dbRoute.route_id,
            route_name: dbRoute.route_name || rname,
          });
        }
      }
    });
    onSave({ toAdd, toRemove });
  };

  return (
    <BottomSheet
      visible={visible}
      title={`Select Locality · ${db?.db_name || "DB"}`}
      subtitle="Tap to toggle. A locality can serve multiple DBs."
      onClose={onClose}
      footer={
        <SheetButtons
          onCancel={onClose}
          onConfirm={handleConfirm}
          loading={saving}
          disabled={!dirty}
          confirmLabel={dirty ? "Save Changes" : "No Changes"}
        />
      }
    >
      {loading && routes.length === 0 ? (
        <Text variant="caption_2" style={styles.empty}>
          Loading localities…
        </Text>
      ) : routes.length === 0 ? (
        <Text variant="caption_2" style={styles.empty}>
          No localities configured at this spoke.
        </Text>
      ) : (
        <ScrollView style={styles.scroll} keyboardShouldPersistTaps="handled">
          {routes.map((r) => {
            const rid = String(r?.route_name);
            const isSel = selected.has(rid);
            const others = assignedElsewhereByRoute.get(rid) || [];
            const onAnotherDb = others.length > 0;
            const orders = Number(r?.user_count) || 0;
            const crates = Number(r?.crate_count) || 0;
            const rowStyle = [
              styles.row,
              isSel && styles.rowSelected,
              !isSel && onAnotherDb && styles.rowDimmed,
            ];
            return (
              <TouchableOpacity
                key={rid}
                activeOpacity={0.85}
                onPress={() => toggle(rid)}
                style={rowStyle}
              >
                <View
                  style={[styles.checkbox, isSel && styles.checkboxSelected]}
                >
                  {isSel ? (
                    <Text variant="caption_2" style={styles.tick}>
                      ✓
                    </Text>
                  ) : null}
                </View>
                <View style={styles.rowBody}>
                  <Text
                    variant="caption_2"
                    style={styles.rowTitle}
                    numberOfLines={1}
                  >
                    {r.route_name || `Route ${r.route_id}`}
                  </Text>
                  <Text
                    variant="caption_2"
                    style={styles.rowMeta}
                    numberOfLines={1}
                  >
                    {r?.locality ? `${r.locality} · ` : ""}
                    {orders} {orders === 1 ? "order" : "orders"} · {crates}{" "}
                    {crates === 1 ? "crate" : "crates"}
                  </Text>
                  {onAnotherDb ? (
                    <Text variant="caption_2" style={styles.rowOnOther}>
                      Already on: {others.join(", ")}
                    </Text>
                  ) : null}
                </View>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}
    </BottomSheet>
  );
};

export default RoutePickerSheet;

const styles = StyleSheet.create({
  empty: {
    color: tokens.textTertiary,
    fontSize: 13,
    textAlign: "center",
    paddingVertical: tokens.spacing.large,
  },
  scroll: { maxHeight: 420 },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: tokens.spacing.medium,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: tokens.borderLight,
    backgroundColor: tokens.surface,
    marginBottom: 6,
  },
  rowSelected: {
    backgroundColor: tokens.pill.packed.bg,
    borderColor: tokens.pill.packed.border,
  },
  rowDimmed: {
    backgroundColor: tokens.bg,
    borderColor: tokens.borderLight,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: tokens.borderMedium,
    marginRight: tokens.spacing.medium,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.surface,
  },
  checkboxSelected: {
    backgroundColor: tokens.primary.bg,
    borderColor: tokens.primary.bg,
  },
  tick: { color: tokens.primary.fg, fontSize: 14, fontWeight: "700" },
  rowBody: { flex: 1 },
  rowTitle: { color: tokens.textPrimary, fontSize: 13, fontWeight: "600" },
  rowMeta: { color: tokens.textSecondary, fontSize: 11, marginTop: 2 },
  rowOnOther: {
    color: tokens.warning,
    fontSize: 11,
    marginTop: 2,
    fontWeight: "600",
  },
});
